import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../../context/AuthContext';
import { useAutofillSync } from '../../hooks/useAutofillSync';
import { Field, TextInput } from '../../components/Field';
import Icon from '../../components/Icon';

/**
 * Only ever redirect to a path inside this console.
 *
 * The value comes from the router's own location state rather than a query
 * string, so it is not attacker-controlled today — but a browser reads a path
 * beginning with a double slash (or slash-backslash) as a protocol-relative URL
 * to another host, so those are rejected here rather than relying on that
 * staying true.
 */
function safeRedirect(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return '/dashboard';
  const second = pathname[1];
  if (second === '/' || second === '\\') return '/dashboard';
  return pathname;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '', password: '' }, mode: 'onSubmit' });

  // Browser autofill does not fire the events react-hook-form listens for, so
  // the DOM is treated as the source of truth for these two fields.
  const { formRef, readDom } = useAutofillSync({ setValue, fields: ['email', 'password'] });

  const onSubmit = async (values) => {
    setServerError(null);
    try {
      await login({
        email: readDom('email', values.email),
        password: readDom('password', values.password),
      });
      toast.success('Welcome back');
      // Return them to whatever they were trying to open before signing in.
      navigate(safeRedirect(location.state?.from?.pathname), { replace: true });
    } catch (err) {
      // Field-level messages where the API gave them; a banner otherwise.
      const fields = err.fieldErrors || {};
      Object.entries(fields).forEach(([field, message]) => setError(field, { message }));
      if (!Object.keys(fields).length) setServerError(err.message);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <img className="auth-logo-img" src="/photos/logo.png" alt="My Desi Guru" />
        <h1 className="auth-title">Sign in</h1>
        <p className="auth-sub">My Desi Guru — super admin console</p>

        {serverError && (
          <div
            role="alert"
            style={{
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              padding: '11px 14px',
              borderRadius: 'var(--radius)',
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 18,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <Icon name="error" style={{ fontSize: 18, flex: '0 0 auto' }} />
            <span>{serverError}</span>
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Field label="Email" error={errors.email?.message} required>
            <TextInput
              type="email"
              autoComplete="username"
              placeholder="you@mydesiguru.com"
              autoFocus
              error={errors.email}
              {...register('email', {
                // Validators fall back to the DOM value, so a credential the
                // browser autofilled without firing an input event still counts
                // as filled in.
                validate: (v) => {
                  const value = v || readDom('email', '');
                  if (!value) return 'Enter your email address';
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return 'Enter a valid email address';
                  return true;
                },
              })}
            />
          </Field>

          <Field label="Password" error={errors.password?.message} required>
            <div style={{ position: 'relative' }}>
              <TextInput
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Your password"
                error={errors.password}
                style={{ paddingRight: 44 }}
                {...register('password', {
                  validate: (v) => (v || readDom('password', '') ? true : 'Enter your password'),
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 0,
                  color: 'var(--ink-muted)',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Icon name={showPassword ? 'visibility_off' : 'visibility'} style={{ fontSize: 19 }} />
              </button>
            </div>
          </Field>

          <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 18 }}>
            <Link to="/forgot-password" style={{ fontSize: 12, fontWeight: 600 }}>
              Forgot your password?
            </Link>
          </div>

          <button type="submit" className="btn-brand" style={{ width: '100%', justifyContent: 'center' }} disabled={isSubmitting}>
            {isSubmitting && <span className="spinner" />}
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
